import type {FunctionComponent, HTMLAttributes, SyntheticEvent} from 'react'
import {Fragment, useEffect, useRef, useState} from 'react'
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from '@headlessui/react'
import {shareOnMobile} from 'react-mobile-share'
import {EmailShareButton, FacebookShareButton, TwitterShareButton, WhatsappShareButton} from 'react-share'
import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import {IconShare} from '~/icons/brg-icon-share'
import {IconFacebook} from '~/icons/brg-icon-share-facebook'
import {IconWhatsup} from '~/icons/brg-icon-share-whatsapp'
import {IconCopy} from '~/icons/brg-icon-share-copy'
import {IconSuccess} from '~/icons/brg-icon-share-success'
import {IconMail} from '~/icons/brg-icon-share-email'
import {useReportWidgetClick} from '~/hooks/analytic-hooks'
import type {RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {IconShareGray} from '~/icons/brg-icon-share-gray'
import {getDomainConfig} from '~/domains'

async function copyTextToClipboard(url: string) {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(url)
  }
}

interface ShareMenuProps extends HTMLAttributes<HTMLDivElement> {
  onHover: (elementName: string, variation: string) => void
  placement: string
  shouldShowNewIcon?: boolean
}

const ShareMenu: FunctionComponent<ShareMenuProps> = ({
  onHover,
  shouldShowNewIcon = false,
  placement,
  ...attributes
}) => {
  const {deviceType, locale, language, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const [isShareLinkCopied, setIsShareLinkCopied] = useState(false)
  const [currentUrl, setCurrentUrl] = useState('')
  const shareMenuButtonRef = useRef<HTMLButtonElement>(null)
  const reportWidgetClick = useReportWidgetClick()
  const translations = getTranslation(language)
  const {styles: stylesBydomain, siteName} = getDomainConfig(domain, locale)

  useEffect(() => {
    if (window && window?.location) {
      const urlToShare = new URL(window.location.href)

      urlToShare.searchParams.set('utm_source', 'share_menu')
      urlToShare.searchParams.set('utm_medium', placement)
      urlToShare.searchParams.set('utm_campaign', 'winner_first')

      setCurrentUrl(urlToShare.toString())
    }
  }, [placement])

  const handleShareItemClick = (clickTrigger: string) => {
    reportWidgetClick({
      widget_name: 'share-menu',
      widget_place: 'top',
      trigger_type: clickTrigger,
    })
    if (shareMenuButtonRef.current) {
      shareMenuButtonRef?.current?.click()
    }
  }

  const handleCopyClick = async (e: SyntheticEvent) => {
    reportWidgetClick({
      widget_name: 'share-menu',
      widget_place: 'top',
      trigger_type: 'copy-link',
    })
    e.stopPropagation()
    e.preventDefault()
    await copyTextToClipboard(currentUrl)
    setIsShareLinkCopied(true)
    setTimeout(() => {
      setIsShareLinkCopied(false)
    }, 2000)
    setTimeout(() => {
      if (shareMenuButtonRef.current) {
        shareMenuButtonRef?.current?.click()
      }
    }, 1500)
  }
  return (
    <div {...attributes}>
      <Menu as="div" className="relative mr-24 inline-block text-left mobile:mr-0">
        <MenuButton
          aria-label="open share menu"
          data-testid="share-menu-button"
          id="share-button"
          ref={shareMenuButtonRef}
          onClick={(e) => {
            reportWidgetClick({
              widget_name: 'share-menu',
              widget_place: 'top',
              trigger_type: 'share-icon',
            })
            if (deviceType !== 'desktop') {
              shareOnMobile({
                url: currentUrl,
                title: siteName,
              })
              e.stopPropagation()
              e.preventDefault()
            }
          }}
          onMouseEnter={() => onHover('share-menu', 'share-icon')}
        >
          {shouldShowNewIcon ? (
            <IconShareGray
              className={clsx(
                'share-icon size-[38px] rounded-full border border-white p-[10px]',
                stylesBydomain.LISTPAGE_WIDGETS_SHAREMENU_BUTTON,
                'listpage_widgets_sharemenu_button',
              )}
            />
          ) : (
            <IconShare className="stroke-black hover:stroke-neutral-600" />
          )}
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transform transition ease-out duration-100"
          enterFrom="transform scale-y-0 origin-top opacity-0"
          enterTo="transform scale-y-1 origin-top opacity-100"
          leave="transform transition ease-in duration-100"
          leaveFrom="transform scale-y-1 origin-top opacity-100"
          leaveTo="transform scale-y-0 origin-top opacity-0"
        >
          <MenuItems
            data-testid="share-menu"
            className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            <MenuItem>
              {({focus}) => (
                <div
                  className={`${
                    focus
                      ? 'group flex w-full items-center rounded-md bg-neutral-100 p-3 text-sm'
                      : 'group flex w-full items-center rounded-md p-3 text-sm text-gray-900'
                  } `}
                >
                  <button
                    type="button"
                    aria-label="copy url fo share"
                    data-testid="copy-link"
                    className="flex w-full flex-row items-center gap-4"
                    onClick={handleCopyClick}
                    onMouseEnter={() => onHover('share-menu', 'copy-link')}
                  >
                    {isShareLinkCopied ? (
                      <>
                        <IconSuccess width="29px" height="29px" />
                        <p>Link copied</p>
                      </>
                    ) : (
                      <>
                        <IconCopy width="29px" height="29px" />
                        <p>{translations.Header.shareMenuCopyText}</p>
                      </>
                    )}
                  </button>
                </div>
              )}
            </MenuItem>
            <MenuItem>
              {({focus}) => (
                <div
                  className={`${
                    focus
                      ? 'group flex w-full items-center rounded-md bg-neutral-100 p-3 text-sm'
                      : 'group flex w-full items-center rounded-md p-3 text-sm text-gray-900'
                  } `}
                  onClick={() => handleShareItemClick('email')}
                  onMouseEnter={() => onHover('share-menu', 'email')}
                >
                  <EmailShareButton url={currentUrl} className="flex w-full flex-row items-center gap-4">
                    <IconMail width="28px" height="28px" />
                    <p>Email</p>
                  </EmailShareButton>
                </div>
              )}
            </MenuItem>
            <MenuItem>
              {({focus}) => (
                <div
                  className={`${
                    focus
                      ? 'group flex w-full items-center rounded-md bg-neutral-100 p-3 text-sm'
                      : 'group flex w-full items-center rounded-md p-3 text-sm text-gray-900'
                  } `}
                  onClick={() => handleShareItemClick('whatsapp')}
                  onMouseEnter={() => onHover('sharemenu', 'whatsapp')}
                >
                  <WhatsappShareButton url={currentUrl} className="flex w-full flex-row items-center gap-3">
                    <IconWhatsup width="32px" height="32px" />
                    <p>WhatsApp</p>
                  </WhatsappShareButton>
                </div>
              )}
            </MenuItem>
            <MenuItem>
              {({focus}) => (
                <div
                  className={`${
                    focus
                      ? 'group flex w-full items-center rounded-md bg-neutral-100 p-3 text-sm'
                      : 'group flex w-full items-center rounded-md p-3 text-sm text-gray-900'
                  } `}
                  onClick={() => handleShareItemClick('facebook')}
                  onMouseEnter={() => onHover('share-menu', 'facebook')}
                >
                  <FacebookShareButton url={currentUrl} className="flex w-full flex-row items-center gap-4">
                    <IconFacebook width="28px" height="28px" />
                    <p>Facebook</p>
                  </FacebookShareButton>
                </div>
              )}
            </MenuItem>
            <MenuItem>
              {({focus}) => (
                <div
                  className={`${
                    focus
                      ? 'group flex w-full items-center rounded-md bg-neutral-100 p-3 text-sm'
                      : 'group flex w-full items-center rounded-md p-3 text-sm text-gray-900'
                  } `}
                  onClick={() => handleShareItemClick('twitter')}
                  onMouseEnter={() => onHover('share-menu', 'twitter')}
                >
                  <TwitterShareButton url={currentUrl} className="flex w-full flex-row items-center gap-4">
                    <img
                      width="28px"
                      height="28px"
                      src="https://res.cloudinary.com/roundforest/image/upload/q_auto:best/v1701368104/BRG/Screenshot_2023-11-30_at_20.13.20.png"
                      alt="twitter"
                    />
                    <p>X (Twitter)</p>
                  </TwitterShareButton>
                </div>
              )}
            </MenuItem>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  )
}

export default ShareMenu
